import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { softDeleteWaypoint, getPointsPassageForMission } from '../../api/commande';
import { getAllSignalementForAdresse } from "../../api/signalements"
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import moment from 'moment'
import { convertMinutesToHHMM, isValidTimeFormat, typeLabels } from '../../utils/formatters';
import { AlertTriangle, Trash2, X, Flag, Truck } from 'lucide-react';
import { BASE_URL } from '../../api/base'
import { useTranslation } from 'react-i18next';





// Styles
const styles = {
    modalContent: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        padding: 0,
        borderRadius: '10px',
        border: 'none',
        fontFamily: 'Montserrat, sans-serif',
        maxHeight: '80vh',
        overflow: 'hidden',
        width: '70%',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #5BBCEA 0%, #51C7F2 100%)',
        padding: '20px',
        position: 'relative',
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px'
    },
    closeButton: {
        position: 'absolute',
        right: '15px',
        backgroundColor: 'transparent',
        padding: '8px',
        color: '#fff',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
    },
    waypoint: {
        background: '#fff',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '15px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)'
        }
    },
    waypointInactive: {
        backgroundColor: '#f5f5f5',
        opacity: 0.7
    },
    label: {
        fontSize: '0.8rem',
        color: '#4A5568',
        marginBottom: '8px',
        lineHeight: '1.5'
    },
    highlight: {
        color: '#FF5A5F',
        fontWeight: '600',
        fontSize: '0.8rem',
    },
    actionButtons: {
        display: 'flex',
        gap: '10px',
        marginTop: '12px'
    },
    iconButton: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: '#f3f4f6'
        }
    },
    container: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    reportButton: {
        backgroundColor: '#FEF3C7',
        color: '#D97706',
        padding: '6px 12px',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        cursor: 'pointer',
        border: '1px solid #FDE68A',
        '&:hover': {
            backgroundColor: '#FEF9C3'
        }
    },
    depotBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: '#E0F2FE',
        color: '#0369A1',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        marginLeft: '10px'
    }
};


// Report Modal Component
const ReportModal = ({ isOpen, onClose, placeId, token }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation()
    useEffect(() => {
        const fetchReports = async () => {
            if (!placeId) return;

            setLoading(true);
            try {
                const response = await getAllSignalementForAdresse(placeId, token);
                setReports(response.data);
            } catch (error) {
                toast.error(t("report_err"), {
                    style: { fontFamily: 'Montserrat' },
                });
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchReports();
        }
    }, [placeId, isOpen, token]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={{
                content: {
                    ...styles.modalContent,
                    width: '50%'
                }
            }}
        >
            <div style={styles.header}>
                <h3 style={{ color: 'white', margin: 0 }}>{t("reports")}</h3>
                <button onClick={onClose} style={styles.closeButton}>
                    <X size={20} />
                </button>
            </div>
            <div style={{ padding: '20px', maxHeight: '60vh', overflowY: 'auto' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <div className='customLoader'></div>
                    </div>
                ) : reports.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        {t("report_err2")}
                    </div>
                ) : (
                    reports.map((report, index) => (
                        <div
                            key={report._id || index}
                            style={{
                                padding: '15px',
                                borderBottom: '1px solid #eee',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                marginBottom: '10px'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                <AlertTriangle size={18} color="#F59E0B" />
                                <span style={{ fontWeight: '600' }}>{typeLabels[report.type]}</span>
                            </div>
                            <p style={{ margin: '0', color: '#4B5563' }}>{report.note}</p>
                            {report.files && report.files.length > 0 && (
                                <div style={{ marginTop: '10px' }}>
                                    <p style={{ fontWeight: '500', color: '#4B5563', marginBottom: '5px' }}>Fichiers joints:</p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                        {report.files.map((file, fileIndex) => (
                                            <a
                                                key={fileIndex}
                                                href={`${BASE_URL}/${file?.path}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: '4px 8px',
                                                    backgroundColor: '#EDF2F7',
                                                    borderRadius: '4px',
                                                    color: '#4A5568',
                                                    textDecoration: 'none',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                {file.name}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                                {t("reported_on")}: {moment(report.createdAt).format('DD/MM/YYYY HH:mm')}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </Modal>
    );
};


const PointsPassageModal = ({ isOpen, onClose, missionId }) => {
    const [modalIsOpenDelete, setModalIsOpenDelete] = useState(false)
    const [selectedPlace, setSelectedPlace] = useState(null)
    const [places, setPlaces] = useState([])
    const [loading, setLoading] = useState(false)
    const [chargementEnCours, setChargementEnCours] = useState(false)
    const [modalIsOpenReport, setModalIsOpenReport] = useState(false);
    const user = useSelector((state) => state.auth.user);
    const token = useSelector((state) => state.auth.token);
    const {t} = useTranslation()
    const fetchPlaces = async (missionId) => {
        setChargementEnCours(true)
        try {
            const res = await getPointsPassageForMission(missionId, token)
            setPlaces(res.pointsPassages)
        } catch (error) {
            toast.error(t("transfer_request_err"), {
                style: {
                    fontFamily: 'Montserrat',
                },
            });
        } finally {
            setChargementEnCours(false)
        }
    }


    useEffect(() => {
        if (missionId && isOpen) {
            fetchPlaces(missionId)
        }
    }, [missionId, isOpen])
    const handleDelete = async () => {
        setLoading(true)
        try {
            const res = await softDeleteWaypoint(token, selectedPlace)
            toast.success(res.message, {
                style: {
                    fontFamily: 'Montserrat',
                },
            })
            setPlaces([])
            await fetchPlaces(missionId)
        } catch (error) {
            console.log("error", error)
            toast.error(error.response?.data?.error || error.response?.data?.message || t("transfer_request_err"), {
                style: { fontFamily: 'Montserrat' },
            });
        } finally {
            setModalIsOpenDelete(false);
            setSelectedPlace(null);
            setLoading(false);
        }
    }
    const [isSmallScreen, setSmallScreen] = useState(() => window.innerWidth < 768);


    useEffect(() => {
        const checkIfMobile = () => {
            setSmallScreen(window.innerWidth < 768);
        };
        window.addEventListener('resize', checkIfMobile);

        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);
    return (
        <>
            <Modal
                isOpen={isOpen}
                onRequestClose={() => {
                    onClose()
                    setPlaces([])
                }}
                ariaHideApp={false}
                style={{
                    content: {
                        ...styles.modalContent,
                        width: isSmallScreen ? '90%' : '60%'
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        zIndex: 1000,
                    }
                }}
            >
                <div style={styles.header}>
                    <h3 style={{ color: 'white', margin: 0, flex: 1, textAlign: 'center' }}>{t("stop_point")} </h3>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X size={20} />
                    </button>
                </div>
                <div style={{
                    padding: '20px',
                    background: '#F7F8F8',
                    overflowY: 'auto',
                    maxHeight: 'calc(80vh - 60px)'
                }}>
                    {chargementEnCours ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                            <div className='customLoader'></div>
                        </div>
                    )
                        : places?.length === 0 ?
                            <div style={{
                                ...styles.waypoint,
                                textAlign: 'center',
                                color: '#666'
                            }} >{t("stop_points_err")}</div>
                            :
                            places.map((place, index) => (
                                <div key={index}
                                    style={{
                                        ...styles.waypoint,
                                    }}
                                >
                                    <div style={isSmallScreen ? {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '5px'
                                    } : styles.container}>


                                        <div style={isSmallScreen ? {
                                            display: 'flex', flexDirection: 'column', fontSize: '0.8rem',
                                            color: '#4A5568',
                                        } : styles.label}>
                                            <span><strong>{t("address")} :</strong>{' '}
                                                <span style={styles.highlight}>{place.adresse.label}</span></span>
                                            <span><strong style={isSmallScreen ? {} : { marginLeft: "1rem" }}>Distance :</strong>{' '}
                                                <span style={styles.highlight}>{place.distance} Km</span></span>
                                            <span><strong style={isSmallScreen ? {} : { marginLeft: "1rem" }}>{t("duration")} :</strong>{' '}
                                                <span style={styles.highlight}>
                                                    {place?.heure && isValidTimeFormat(place.heure) ? place.heure : convertMinutesToHHMM(place?.heure || 0)}
                                                </span></span>
                                            {place?.signé && place?.date_signature && (
                                                <>
                                                    <span><strong style={isSmallScreen ? {} : { marginLeft: "1rem" }}>{t("date_of_signature")} :</strong>{' '}
                                                        <span style={styles.highlight}>
                                                            {moment(place.date_signature).format('DD/MM/YYYY HH:mm')}
                                                        </span>
                                                    </span>
                                                </>
                                            )}
                                        </div>

                                        {place.depot && (
                                            <span style={isSmallScreen ? {
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                backgroundColor: '#E0F2FE',
                                                color: '#0369A1',
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            } : styles.depotBadge}>
                                                <Truck size={16} />
                                                {t("drop_off_point")}
                                            </span>
                                        )}
                                    </div>

                                    {!place.depot && (
                                        <>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                                <div style={isSmallScreen ? {
                                                    display: 'flex', flexDirection: 'column', fontSize: '0.8rem',
                                                    color: '#4A5568',
                                                } : styles.label}>
                                                    <span><strong>{t("signed")} :</strong> {place?.signé ? t("yes") : t("no")}</span>
                                                    <span> <strong style={isSmallScreen ? {} : { marginLeft: "1rem" }}>{t("reported")} :</strong> {place?.signaler ? t("yes") : t("no")}</span>
                                                    <span><strong style={isSmallScreen ? {} : { marginLeft: "1rem" }}>{t("limit_exceeded")} :</strong> {place?.active ? t("yes") : t("no")}</span>
                                                </div>

                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    {/* Report Button */}
                                                    {place?.signaler &&
                                                        <button
                                                            onClick={() => {
                                                                setSelectedPlace(place._id);
                                                                setModalIsOpenReport(true);
                                                            }}
                                                            style={styles.reportButton}
                                                        >
                                                            <Flag size={16} />
                                                            {t("reports")}
                                                        </button>
                                                    }


                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                </div>
                <Modal
                    isOpen={modalIsOpenDelete}
                    style={{
                        overlay: {
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        },
                        content: {
                            top: '50%',
                            left: '50%',
                            right: 'auto',
                            bottom: 'auto',
                            marginRight: '-50%',
                            transform: 'translate(-50%, -50%)',
                            borderRadius: 19,
                            padding: '2rem',
                        },
                    }}
                    contentLabel='Example Modal'
                >
                    <div
                        style={{
                            width: '20rem',
                            fontFamily: 'Montserrat',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 15,
                            textAlign: 'center',
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 'bold',
                                fontFamily: 'Montserrat',
                                fontSize: 20,
                                color: '#51C7F2',
                            }}
                        > {t("delete_address")}</div>
                        <div
                            style={{
                                marginTop: 4,
                                width: '100%',
                            }}
                        >

                            <p>{t("delete_address_err")} </p>

                        </div>
                        <div
                            style={{
                                width: '100%',
                                fontFamily: 'Montserrat',
                                paddingLeft: '1rem',
                                paddingRight: '1rem',
                                marginBottom: '5px',
                                marginTop: '1rem',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                            }}>
                            <button
                                onClick={handleDelete}
                                disabled={loading}
                                style={{
                                    width: "150px",
                                    height: "50px",
                                    background: '#50F5A9',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'white',
                                    borderRadius: 7,
                                    marginTop: 5,
                                    fontFamily: 'Montserrat',
                                    marginRight: 10,
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}

                            >

                                {loading ? (
                                    <div className='loader'></div>
                                ) : (
                                    t("confirm")
                                )}

                            </button>
                            <button
                                onClick={() => {
                                    setModalIsOpenDelete(false)

                                }}
                                style={{
                                    width: "150px",
                                    height: "50px",
                                    background: 'red',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'white',
                                    borderRadius: 7,
                                    marginTop: 5,
                                    fontFamily: 'Montserrat',
                                    fontWeight: 'bold',
                                }}
                            >
                                {t("cancel")}
                            </button>
                        </div>

                    </div>
                </Modal>
            </Modal>

            {/* Updated Reports Modal */}
            <ReportModal
                isOpen={modalIsOpenReport}
                onClose={() => {
                    setModalIsOpenReport(false);
                    setSelectedPlace(null);
                }}
                placeId={selectedPlace}
                token={token}
            />
        </>

    );

};

export default PointsPassageModal;
